
import { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, CheckCircle, AlertTriangle } from "lucide-react";

interface RecoveryData {
  token: string | null;
  email: string | null;
  tokenHash: string | null;
  type: string | null;
}

function parseRecoveryData(
  payload: string | undefined,
  searchParams: URLSearchParams
): RecoveryData {
  // 1. Path payload (base64url-encoded JSON)
  if (payload) {
    try {
      // Restore base64 padding and decode
      let b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      while (b64.length % 4) b64 += '=';
      const json = atob(b64);
      const obj = JSON.parse(json);
      if (obj.token && obj.email) {
        console.log('[ResetPassword] Parsed recovery data from path payload');
        return { token: obj.token, email: obj.email, tokenHash: null, type: obj.type || 'recovery' };
      }
    } catch (e) {
      console.warn('[ResetPassword] Failed to parse path payload:', e);
    }
  }

  // 2. Query params
  const qToken = searchParams.get('token');
  const qEmail = searchParams.get('email');
  const qTokenHash = searchParams.get('token_hash');
  const qType = searchParams.get('type');
  if (qToken && qEmail) {
    console.log('[ResetPassword] Parsed recovery data from query params');
    return { token: qToken, email: qEmail, tokenHash: qTokenHash, type: qType };
  }

  // 3. Hash params
  const hashStr = window.location.hash.substring(1);
  if (hashStr) {
    const hashParams = new URLSearchParams(hashStr);
    const hToken = hashParams.get('token');
    const hEmail = hashParams.get('email');
    const hTokenHash = hashParams.get('token_hash');
    const hType = hashParams.get('type');
    if (hToken && hEmail) {
      console.log('[ResetPassword] Parsed recovery data from hash params');
      return { token: hToken, email: hEmail, tokenHash: hTokenHash, type: hType };
    }
    // Legacy hash with access_token
    if (hashParams.get('access_token') && hashParams.get('type') === 'recovery') {
      return { token: null, email: null, tokenHash: null, type: 'recovery' };
    }
  }

  // 4. Legacy query: token_hash only
  if (searchParams.get('token_hash') && searchParams.get('type') === 'recovery') {
    console.log('[ResetPassword] Parsed recovery data from legacy token_hash');
    return { token: null, email: null, tokenHash: searchParams.get('token_hash'), type: 'recovery' };
  }

  // 5. Legacy fallback: ?confirmation_url=https://...supabase.co/auth/v1/verify?token=...&type=recovery
  // Extract the embedded token WITHOUT auto-calling verify (so Outlook Safe Links can't consume it).
  const confirmationUrl = searchParams.get('confirmation_url');
  if (confirmationUrl) {
    try {
      const inner = new URL(confirmationUrl);
      const innerToken = inner.searchParams.get('token');
      const innerType = inner.searchParams.get('type');
      const innerTokenHash = inner.searchParams.get('token_hash');
      if (innerToken && innerType === 'recovery') {
        console.log('[ResetPassword] Parsed recovery data from confirmation_url (token_hash)');
        // Supabase /auth/v1/verify uses token_hash semantics for the opaque token
        return { token: null, email: null, tokenHash: innerToken, type: 'recovery' };
      }
      if (innerTokenHash && innerType === 'recovery') {
        console.log('[ResetPassword] Parsed recovery data from confirmation_url (explicit token_hash)');
        return { token: null, email: null, tokenHash: innerTokenHash, type: 'recovery' };
      }
    } catch (e) {
      console.warn('[ResetPassword] Failed to parse confirmation_url:', e);
    }
  }

  // 6. PKCE code
  if (searchParams.get('code')) {
    console.log('[ResetPassword] Parsed PKCE code');
    return { token: null, email: null, tokenHash: null, type: 'code' };
  }

  console.warn('[ResetPassword] No recovery data found in URL', {
    pathPayload: !!payload,
    queryKeys: Array.from(searchParams.keys()),
    hash: window.location.hash,
  });
  return { token: null, email: null, tokenHash: null, type: null };
}

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const { payload } = useParams<{ payload?: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isReady, setIsReady] = useState(false);
  const [manualEmail, setManualEmail] = useState("");

  const recovery = useMemo(
    () => parseRecoveryData(payload, searchParams),
    [payload, searchParams]
  );

  const hasRecoveryToken = !!(
    (recovery.token && recovery.email) ||
    recovery.tokenHash ||
    recovery.type === 'recovery'
  );

  const hasCode = recovery.type === 'code';

  // Legacy PKCE / implicit flows
  useEffect(() => {
    if (hasRecoveryToken || hasCode === false) return;

    const code = searchParams.get('code');
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session && (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && code))) {
        setIsReady(true);
      }
    });

    if (code) {
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) setIsReady(true);
      });
    }

    return () => { subscription.unsubscribe(); };
  }, [hasRecoveryToken, hasCode, searchParams]);

  // Legacy implicit flow via hash
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const accessToken = hashParams.get('access_token');
    const refreshToken = hashParams.get('refresh_token');
    const hashType = hashParams.get('type');

    if (accessToken && refreshToken && hashType === 'recovery') {
      supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      }).then(({ data: { session }, error }) => {
        if (!error && session) setIsReady(true);
      });
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast({ title: "Lösenorden matchar inte", description: "Kontrollera att båda lösenorden är identiska.", variant: "destructive" });
      return;
    }

    if (newPassword.length < 6) {
      toast({ title: "Lösenordet är för kort", description: "Lösenordet måste vara minst 6 tecken långt.", variant: "destructive" });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      toast({ title: "Lösenord uppdaterat", description: "Ditt lösenord har uppdaterats. Du kommer nu att loggas in." });
      setTimeout(() => navigate('/admin'), 2000);
    } catch (error: any) {
      console.error('Error updating password:', error);
      toast({ title: "Ett fel uppstod", description: error.message || "Kunde inte uppdatera lösenordet.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyToken = async () => {
    setIsVerifying(true);
    try {
      // Primary: raw OTP token + email
      const emailToUse = recovery.email || manualEmail;
      if (recovery.token && emailToUse) {
        console.log('[ResetPassword] Trying verifyOtp with token + email');
        const { data, error } = await supabase.auth.verifyOtp({
          token: recovery.token,
          email: emailToUse,
          type: 'recovery',
        });

        if (!error && data.session) {
          console.log('[ResetPassword] verifyOtp succeeded (token + email)');
          setIsReady(true);
          return;
        }
        console.error('[ResetPassword] verifyOtp token+email failed:', error?.message);
      }

      // Fallback: token_hash
      if (recovery.tokenHash) {
        console.log('[ResetPassword] Trying verifyOtp with token_hash');
        const { data, error } = await supabase.auth.verifyOtp({
          token_hash: recovery.tokenHash,
          type: 'recovery',
        });

        if (!error && data.session) {
          console.log('[ResetPassword] verifyOtp succeeded (token_hash)');
          setIsReady(true);
          return;
        }
        console.error('[ResetPassword] verifyOtp token_hash failed:', error?.message);
      }

      toast({ title: "Ogiltig länk", description: "Återställningslänken är ogiltig eller har gått ut. Begär en ny.", variant: "destructive" });
    } catch (err) {
      console.error('[ResetPassword] verifyOtp error:', err);
      toast({ title: "Ett fel uppstod", description: "Kunde inte verifiera länken. Försök igen.", variant: "destructive" });
    } finally {
      setIsVerifying(false);
    }
  };

  // No recovery data found at all
  if (!hasRecoveryToken && !hasCode && !isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md p-6 space-y-6 text-center">
          <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto" />
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              Länken saknar återställningsdata
            </h1>
            <p className="text-sm text-muted-foreground">
              Det verkar som att din e-postklient har ändrat länken. Begär en ny återställningslänk eller försök kopiera och klistra in hela länken från mejlet.
            </p>
          </div>
          <Button className="w-full" onClick={() => navigate('/admin')}>
            Begär ny länk
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => navigate('/admin')}>
            Tillbaka till inloggning
          </Button>
        </Card>
      </div>
    );
  }

  // Has token but missing email — ask user to provide it
  if (hasRecoveryToken && recovery.token && !recovery.email && !isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md p-6 space-y-6 text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              Bekräfta din e-postadress
            </h1>
            <p className="text-sm text-muted-foreground">
              Ange din e-postadress för att fortsätta med lösenordsåterställningen.
            </p>
          </div>
          <div className="space-y-2">
            <Input
              type="email"
              placeholder="din@email.se"
              value={manualEmail}
              onChange={(e) => setManualEmail(e.target.value)}
            />
          </div>
          <Button onClick={handleVerifyToken} disabled={isVerifying || !manualEmail} className="w-full">
            {isVerifying ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Verifierar...</span>
              </div>
            ) : "Fortsätt"}
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => navigate('/admin')}>
            Avbryt
          </Button>
        </Card>
      </div>
    );
  }

  // Standard confirm step (token + email available)
  if (hasRecoveryToken && !isReady) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <Card className="w-full max-w-md p-6 space-y-6 text-center">
          <CheckCircle className="h-12 w-12 text-green-500 mx-auto" />
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              Bekräfta lösenordsåterställning
            </h1>
            <p className="text-sm text-muted-foreground">
              Klicka på knappen nedan för att fortsätta till lösenordsformuläret.
            </p>
          </div>
          <Button onClick={handleVerifyToken} disabled={isVerifying} className="w-full">
            {isVerifying ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Verifierar...</span>
              </div>
            ) : "Fortsätt"}
          </Button>
          <Button variant="ghost" className="w-full" onClick={() => navigate('/admin')}>
            Avbryt
          </Button>
        </Card>
      </div>
    );
  }

  if (!isReady) return null;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md p-6 space-y-6">
        <div className="space-y-2 text-center">
          <Lock className="h-12 w-12 text-primary mx-auto" />
          <h1 className="text-2xl font-semibold tracking-tight">
            Återställ lösenord
          </h1>
          <p className="text-sm text-muted-foreground">
            Ange ditt nya lösenord nedan.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="newPassword">Nytt lösenord</Label>
            <Input id="newPassword" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="Ange nytt lösenord" required minLength={6} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Bekräfta lösenord</Label>
            <Input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="Bekräfta nytt lösenord" required minLength={6} />
          </div>
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? (
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Uppdaterar...</span>
              </div>
            ) : "Uppdatera lösenord"}
          </Button>
        </form>

        <Button variant="ghost" className="w-full" onClick={() => navigate('/admin')}>
          Tillbaka till inloggning
        </Button>
      </Card>
    </div>
  );
};

export default ResetPassword;
